import useAPIStore from 'apis/common/common.api';
import { FEATURES } from 'consts/feature/const';
import { useAuth } from 'hooks/Auth/useAuth';
import i18n from 'i18n';
import { useEffect, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { ROUTES } from 'router/const';
import { useLocaleStore } from 'stores/useLocaleStore';

/**
 * useBootstrapLanguage
 * -----------------------------
 * - 언어 목록을 API 또는 fallback으로 불러옴
 * - i18n 초기 언어 세팅
 * - 모든 과정이 완료되면 isReady = true 반환
 */
export const useBootstrapLanguage = () => {
  const location = useLocation();

  const isEmbed = !!matchPath({ path: ROUTES.EMBED.PATH, end: false }, location.pathname);

  const isExperimentalMode = FEATURES.I18N_DEV_MODE;

  const { getLanguages } = useAPIStore();
  const { locale, isResourceTestMode, setLanguages, setLocale, setIsResourceTestMode } =
    useLocaleStore();
  const { token, user } = useAuth();
  const [isReady, setIsReady] = useState(!isExperimentalMode);
  const isReadyRef = useRef(isReady);

  const init = async () => {
    if (!isExperimentalMode || isEmbed) return;

    try {
      const response = await getLanguages();

      if (response.isSuccess) {
        setLanguages(response.resultList);
      } else {
        setLanguages([
          { locale: 'ko-KR', displayName: '한국어', languageCode: 'KO' },
          { locale: 'en-US', displayName: 'English', languageCode: 'EN' },
        ]);
      }
    } catch {}
  };

  useEffect(() => {
    if (isEmbed) {
      setIsReady(true);
      return;
    }

    init();
    i18n.on('languageChanged', setLocale);
  }, []);

  useEffect(() => {
    isReadyRef.current = isReady;
  }, [isReady]);

  // * Access Token 상태 감시 및 전역 동기화
  useEffect(() => {
    if (isEmbed || !token) {
      i18n.changeLanguage(locale).then(() => {
        if (!isReady) setIsReady(true);
      });
      return;
    }

    const { activeLanguage } = token;
    i18n.changeLanguage(activeLanguage).then(() => {
      if (!isReady) setIsReady(true);
    });
  }, [token, isEmbed]);

  // * 임시 테스트용 코드
  // * 임시 테스트용 코드
  // * 임시 테스트용 코드
  useEffect(() => {
    if (isEmbed) return;

    if (user?.loginId !== 'resource') {
      setIsResourceTestMode(false);
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsResourceTestMode(!isResourceTestMode);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isResourceTestMode, user?.loginId]);

  return isReady;
};
